// A run's re-grade on its page (TOM-257, ADR 0254): why its results need
// grading (a judge that failed, in plain words), the runs that already grade
// it again, and the re-grade itself. The admin picks the judges as on the
// launch page (the same fields), sets a required budget, reads the estimate
// (POST /v1/admin/evals/runs/:id/regrade/estimate, which writes nothing) and
// confirms with a second click. The re-grade runs on the worker; the page then
// moves to the new run, whose page shows its live progress and Cancel, and
// whose details link back to the run it grades ("Kind: regrade of …").

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { adminApiError, adminApiMessage } from '../adminApi';
import { formatDate } from '../format';
import { EvalError, RunLink, StatusPill } from './EvalBits';
import { EvalBudgetField, EvalJudgeFields } from './EvalJudgeFields';
import { EvalLaunchEstimateView } from './EvalLaunchEstimate';
import { formatUsd, modelShortName } from './format';
import { EVAL_NOTES_MAX_CHARS, heldOutCount, type EvalLaunchOptions } from './launch';
import { evalLaunchApi } from './launchApi';
import { runHref } from './query';
import {
  defaultRegradeForm,
  regradeErrorText,
  regradeOverBudgetText,
  regradeRequestFromForm,
  regradeRequestKey,
  regradeSourceText,
  type EvalRegradeEstimate,
  type EvalRegradeForm,
  type EvalRegradeOffer,
  type EvalRegradeRequest,
} from './regrade';
import type { EvalRunDetail, EvalRunListItem } from './types';
import '../../styles/admin-evals-launch.css';
import '../../styles/admin-evals-regrade.css';

/** How long the form rests before the estimate is asked for again (as on the launch page). */
const ESTIMATE_DEBOUNCE_MS = 400;

/** The judges that grade a run's models, as its model rows name them. */
function judgesText(r: EvalRunListItem): string {
  const judges = [
    ...new Set(
      r.models.flatMap((m) =>
        [m.judgeModel, m.secondJudgeModel].filter((j): j is string => j !== null)
      )
    ),
  ];
  if (judges.length > 0) return judges.map(modelShortName).join(', ');
  return r.models.some((m) => m.grading === 'heuristic') ? 'Phrase heuristic' : 'None';
}

/** The runs that grade this one again, newest first. */
function RegradeTable({ regrades }: { regrades: EvalRunListItem[] }) {
  return (
    <>
      <h3 className="eval-regrade-caption">Re-grades of this run</h3>
      <div className="scroll-x">
        <table className="admin-table eval-table--compact" data-testid="eval-regrades">
          <thead>
            <tr>
              <th>Run</th>
              <th>Status</th>
              <th>Judges</th>
              <th>Graded</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {regrades.map((r) => {
              const planned = r.models.reduce((s, m) => s + m.plannedResults, 0);
              const complete = r.models.reduce((s, m) => s + m.complete, 0);
              return (
                <tr key={r.id} data-testid={`eval-regrade-row-${r.id}`}>
                  <td>
                    <span className="eval-stack">
                      <RunLink id={r.id} />
                      <span className="admin-muted eval-sub eval-nowrap">
                        {formatDate(r.startedAt ?? r.createdAt)}
                      </span>
                    </span>
                  </td>
                  <td>
                    <StatusPill status={r.status} stalled={r.stalled} />
                  </td>
                  <td>{judgesText(r)}</td>
                  <td className="eval-nowrap">
                    {complete} of {planned}
                  </td>
                  <td className="eval-nowrap">
                    {formatUsd(r.totalCostUsd)}
                    {r.budgetUsd !== null ? (
                      <span className="admin-muted"> of {formatUsd(r.budgetUsd)}</span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** The held-out test split's second confirmation, in a re-grade's words. */
function RegradeTestSplit({
  estimate,
  form,
  set,
}: {
  estimate: EvalRegradeEstimate | null;
  form: EvalRegradeForm;
  set: (patch: Partial<EvalRegradeForm>) => void;
}) {
  const refusal = estimate?.refusals.find((r) => r.code === 'test_split_unconfirmed') ?? null;
  const n = heldOutCount(refusal);
  if (refusal === null && !form.confirmTestSplit) return null;
  return (
    <div className="eval-confirm-test" data-testid="eval-regrade-confirm-test">
      <p>
        <strong>Held-out test cases.</strong> This run holds cases held out so nobody tunes Neo to
        them. A re-grade measures them again: do it only for a deliberate test measurement.
      </p>
      <label className="eval-launch-option">
        <input
          type="checkbox"
          checked={form.confirmTestSplit}
          data-testid="eval-regrade-confirm-test-split"
          onChange={(e) => set({ confirmTestSplit: e.target.checked })}
        />
        <span>
          Yes, this re-grade measures the held-out test cases again
          {n !== null ? ` (${n} test ${n === 1 ? 'case' : 'cases'})` : ''}.
        </span>
      </label>
    </div>
  );
}

/** The re-grade form: judges and notes, then the budget, the estimate and the two-click go. */
function RegradeForm({
  run,
  options,
  onClose,
}: {
  run: EvalRunDetail['run'];
  options: EvalLaunchOptions;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState<EvalRegradeForm>(() => defaultRegradeForm(options, run.suite));
  const [estimate, setEstimate] = useState<{ key: string; data: EvalRegradeEstimate } | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const seq = useRef(0);

  const built = regradeRequestFromForm(form, options, run.suite);
  const key = built.ok ? regradeRequestKey(built.body) : null;

  useEffect(() => {
    if (key === null) {
      seq.current += 1;
      setEstimating(false);
      return;
    }
    const n = ++seq.current;
    setEstimating(true);
    const timer = window.setTimeout(() => {
      evalLaunchApi
        .regradeEstimate(run.id, JSON.parse(key) as EvalRegradeRequest)
        .then((data) => {
          if (n !== seq.current) return;
          setEstimate({ key, data });
          setEstimateError(null);
        })
        .catch((err) => {
          if (n !== seq.current) return;
          setEstimate(null);
          setEstimateError(regradeErrorText(adminApiError(err)));
        })
        .finally(() => {
          if (n === seq.current) setEstimating(false);
        });
    }, ESTIMATE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [key, run.id]);

  function set(patch: Partial<EvalRegradeForm>) {
    setConfirming(false);
    setLaunchError(null);
    setForm((f) => ({ ...f, ...patch }));
  }

  const fresh = estimate !== null && key !== null && estimate.key === key && !estimating;
  const shown = estimate !== null && key !== null ? estimate.data : null;
  const canLaunch = fresh && estimate.data.refusals.length === 0;
  const overBudget = shown !== null ? regradeOverBudgetText(shown) : null;

  // Two clicks, never a form submit: Enter in a field must not launch a
  // re-grade that spends provider money.
  async function launch() {
    if (launching || !canLaunch || !confirming || !built.ok) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const res = await evalLaunchApi.regrade(run.id, built.body);
      // Started: the form never offers this re-grade again. `launching` stays
      // set, so nothing here can post a second, paid one while the page moves
      // to the new run.
      setConfirming(false);
      navigate(runHref(res.runId));
    } catch (err) {
      setConfirming(false);
      setLaunching(false);
      setLaunchError(regradeErrorText(adminApiError(err)));
    }
  }

  return (
    <form
      className="eval-launch eval-regrade-form"
      onSubmit={(e) => e.preventDefault()}
      noValidate
      data-testid="eval-regrade-form"
    >
      <div className="eval-launch-form">
        <EvalJudgeFields
          judges={options.judges}
          form={form}
          set={set}
          testIdPrefix="eval-regrade"
        />
        <fieldset className="eval-launch-section">
          <legend>Notes</legend>
          <label>
            <span className="eval-launch-label">Why this re-grade (optional)</span>
            <input
              type="text"
              value={form.notes}
              maxLength={EVAL_NOTES_MAX_CHARS}
              placeholder="e.g. the judge ran out of credit"
              onChange={(e) => set({ notes: e.target.value })}
            />
          </label>
        </fieldset>
      </div>

      <aside className="eval-launch-aside" aria-label="Estimate and re-grade">
        <h3>Budget and estimate</h3>
        <EvalBudgetField
          maxBudgetUsd={options.maxBudgetUsd}
          value={form.budgetUsd}
          onChange={(budgetUsd) => set({ budgetUsd })}
          testId="eval-regrade-budget"
        />
        {!built.ok ? (
          <ul className="eval-list eval-launch-problems" data-testid="eval-regrade-problems">
            {built.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        {key !== null && shown === null && estimateError === null ? (
          <p className="admin-loading-row" data-testid="eval-regrade-estimating">
            Estimating…
          </p>
        ) : null}
        {estimateError !== null ? (
          <p className="eval-error-text" role="alert" data-testid="eval-regrade-estimate-error">
            {estimateError}
          </p>
        ) : null}
        {shown !== null ? (
          <>
            <p className="eval-sub eval-regrade-source" data-testid="eval-regrade-source">
              {regradeSourceText(shown.source)}
            </p>
            <EvalLaunchEstimateView estimate={shown} stale={!fresh} />
          </>
        ) : null}
        {overBudget !== null ? (
          <p className="eval-regrade-warn" role="note" data-testid="eval-regrade-over-budget">
            {overBudget}
          </p>
        ) : null}

        <RegradeTestSplit estimate={fresh ? shown : null} form={form} set={set} />

        <div className="eval-launch-go">
          {confirming && canLaunch ? (
            <div className="admin-confirm" data-testid="eval-regrade-confirm">
              <span className="admin-confirm-prompt">
                Re-grade? This can spend up to {formatUsd(shown!.spendCeilingUsd)} of provider money
                on judging (estimate {formatUsd(shown!.estimate.totalUsd)}, budget{' '}
                {formatUsd(shown!.budgetUsd)}).
              </span>
              <button
                type="button"
                className="admin-button-affirm"
                disabled={launching}
                onClick={() => void launch()}
                data-testid="eval-regrade-confirm-button"
              >
                {launching ? 'Starting…' : 'Re-grade'}
              </button>
              <button
                type="button"
                className="admin-link-button"
                onClick={() => setConfirming(false)}
              >
                Back
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="admin-button-affirm"
              disabled={!canLaunch || launching}
              onClick={() => setConfirming(true)}
              data-testid="eval-regrade-submit"
            >
              {launching ? 'Starting…' : 'Re-grade…'}
            </button>
          )}
          {!canLaunch ? (
            <p className="admin-muted eval-sub" data-testid="eval-regrade-blocked">
              {!built.ok
                ? 'Complete the form to re-grade.'
                : estimateError !== null && !estimating
                  ? 'The estimate was refused: see why above.'
                  : !fresh
                    ? 'Waiting for the estimate of the form as it stands.'
                    : `Refused until ${shown!.refusals.length === 1 ? 'the item' : 'every item'} above is fixed.`}
            </p>
          ) : null}
          {launchError !== null ? (
            <p className="eval-error-text" role="alert" data-testid="eval-regrade-error">
              {launchError}
            </p>
          ) : null}
          <button type="button" className="admin-link-button" onClick={onClose}>
            Close without re-grading
          </button>
        </div>
      </aside>
    </form>
  );
}

/**
 * The re-grade section of a run's page: why the run needs grading, its
 * re-grades, and the re-grade form behind one button. Nothing for a run that
 * cannot be re-graded and has no re-grade.
 */
export function EvalRegradeSection({
  run,
  regrades,
  offer,
}: {
  run: EvalRunDetail['run'];
  regrades: EvalRunListItem[];
  offer: EvalRegradeOffer;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<EvalLaunchOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bumped by Try again after the judges failed to load.
  const [loadTry, setLoadTry] = useState(0);

  useEffect(() => {
    if (!open || options !== null) return;
    let ignore = false;
    setLoadError(null);
    evalLaunchApi
      .options()
      .then((o) => {
        if (!ignore) setOptions(o);
      })
      .catch((err) => {
        if (!ignore) setLoadError(adminApiMessage(err, 'Failed to load the judges.'));
      });
    return () => {
      ignore = true;
    };
  }, [open, options, loadTry]);

  if (offer.kind === 'none' && regrades.length === 0) return null;
  const needed = offer.kind === 'offer' && offer.needed;
  const title = needed
    ? 'Finish grading this run'
    : offer.kind === 'none'
      ? 'Re-grades'
      : 'Re-grade';

  return (
    <section
      className="eval-regrade"
      aria-labelledby="eval-regrade-title"
      data-testid="eval-regrade"
    >
      <h2 id="eval-regrade-title">{title}</h2>
      {offer.kind === 'wait' ? (
        <p className="eval-regrade-lead eval-regrade-lead--needed" data-testid="eval-regrade-wait">
          {offer.text}
        </p>
      ) : null}
      {offer.kind === 'nothing' ? (
        <p className="eval-regrade-lead" data-testid="eval-regrade-nothing">
          {offer.text}
        </p>
      ) : null}
      {offer.kind === 'offer' ? (
        <div
          className={`eval-regrade-lead${needed ? ' eval-regrade-lead--needed' : ''}`}
          data-testid="eval-regrade-lead"
        >
          {offer.lines.length > 0 ? (
            <ul className="eval-regrade-why" data-testid="eval-regrade-why">
              {offer.lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          ) : null}
          <p className="eval-regrade-explain">
            A re-grade grades this run&apos;s stored replies again with the judges you pick, into a
            new run. No model is asked again, so it costs only the judging. This run stays as it is.
          </p>
          {!open ? (
            <button
              type="button"
              className={needed ? 'admin-button-affirm' : 'admin-button-secondary'}
              onClick={() => setOpen(true)}
              data-testid="eval-regrade-open"
            >
              Re-grade this run…
            </button>
          ) : null}
        </div>
      ) : null}
      {regrades.length > 0 ? <RegradeTable regrades={regrades} /> : null}
      {open ? (
        <>
          <EvalError message={loadError} />
          {loadError !== null ? (
            <div className="eval-actions" data-testid="eval-regrade-load-failed">
              <button
                type="button"
                className="admin-button-secondary"
                onClick={() => setLoadTry((n) => n + 1)}
              >
                Try again
              </button>
              <button
                type="button"
                className="admin-link-button"
                onClick={() => {
                  setOpen(false);
                  setLoadError(null);
                }}
              >
                Close
              </button>
            </div>
          ) : null}
          {options === null && loadError === null ? (
            <p className="admin-loading-row">Loading the judges…</p>
          ) : null}
          {options !== null ? (
            <RegradeForm run={run} options={options} onClose={() => setOpen(false)} />
          ) : null}
        </>
      ) : null}
    </section>
  );
}
